import { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  isRtl,
  isSupportedLocale,
  type SupportedLocale,
} from '@noxverify/i18n';
import { loadProjectContext, type ApplicantContext } from './modules/context/context-loader';
import { brandingToCssVars, toBrandingConfig } from './modules/context/project-branding';
import { StartPage } from './pages/StartPage';
import { CaseResumePage } from './pages/CaseResumePage';
import { ResubmissionPage } from './pages/ResubmissionPage';
import { PersonalDetailsPage } from './pages/PersonalDetailsPage';
import { UploadDocumentsPage } from './pages/UploadDocumentsPage';
import { SelfiePage } from './pages/SelfiePage';
import { ConfirmationPage } from './pages/ConfirmationPage';
import { BusinessVerificationPage } from './pages/BusinessVerificationPage';
import { MerchantOnboardingPage } from './pages/MerchantOnboardingPage';
import { StepUpPage } from './pages/StepUpPage';

const PORTAL_LOCALE_STORAGE_KEY = 'nv_locale_portal';

// ---------------------------------------------------------------------------
// Shared loading / error screens
// ---------------------------------------------------------------------------

function LoadingScreen() {
  const { t } = useTranslation('portal');
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="text-center">
        <div
          className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
          role="status"
          aria-label={t('app.loading')}
        />
        <p className="font-mono text-xs uppercase tracking-[0.12em] text-ink-muted">
          {t('app.loading')}
        </p>
      </div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  const { t } = useTranslation('portal');
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-5">
      <div className="max-w-md text-center" role="alert">
        <h1 className="mb-3 font-display text-3xl font-medium tracking-tight text-ink">
          {t('app.unableToLoad')}
        </h1>
        <p className="mb-6 leading-relaxed text-ink-muted">{message}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md border border-hairline-strong px-6 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
        >
          {t('app.tryAgain')}
        </button>
      </div>
    </div>
  );
}

function NotFoundPage() {
  const { t } = useTranslation('portal');
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-5">
      <div className="max-w-md text-center">
        <h1 className="mb-3 font-display text-3xl font-medium tracking-tight text-ink">
          {t('app.pageNotFound')}
        </h1>
        <p className="mb-6 leading-relaxed text-ink-muted">
          {t('app.pageNotFoundDescription')}
        </p>
        <a
          href="/"
          className="inline-block rounded-md px-6 py-2.5 text-sm font-semibold text-surface transition-colors hover:brightness-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nv-primary-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          style={{ backgroundColor: 'var(--nv-primary-color)' }}
        >
          {t('app.goToStart')}
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locale resolution helpers
// ---------------------------------------------------------------------------

/**
 * Resolve the initial portal locale at app load.
 *
 * Precedence (first match wins):
 *   1. Tenant `branding.defaultLocale` from the project context.
 *   2. Previously stored user choice (`localStorage[nv_locale_portal]`).
 *   3. `navigator.language` if it (or its base) maps to a supported locale.
 *   4. `'en'`.
 */
function resolveInitialLocale(
  brandingDefault: string | null | undefined,
): SupportedLocale {
  if (brandingDefault && isSupportedLocale(brandingDefault)) {
    return brandingDefault;
  }

  try {
    const stored = window.localStorage.getItem(PORTAL_LOCALE_STORAGE_KEY);
    if (stored && isSupportedLocale(stored)) return stored;
  } catch {
    // Ignore storage failures.
  }

  const navLang = typeof navigator !== 'undefined' ? navigator.language : '';
  if (navLang) {
    if (isSupportedLocale(navLang)) return navLang;
    const base = navLang.split('-')[0];
    if (base && isSupportedLocale(base)) return base;
  }

  return 'en';
}

// ---------------------------------------------------------------------------
// App root
// ---------------------------------------------------------------------------

export function App() {
  const { i18n } = useTranslation('portal');
  const [context, setContext] = useState<ApplicantContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || window.location.origin;
    loadProjectContext(apiBase)
      .then((ctx) => {
        setContext(ctx);

        // Apply branding CSS vars to :root.
        const brandingConfig = toBrandingConfig(ctx.branding);
        const vars = brandingToCssVars(brandingConfig);
        for (const [key, value] of Object.entries(vars)) {
          document.documentElement.style.setProperty(key, value);
        }

        // Resolve initial locale honouring tenant default → user → browser.
        const resolved = resolveInitialLocale(brandingConfig.defaultLocale);
        if (resolved !== i18n.language) {
          void i18n.changeLanguage(resolved);
        }
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load'),
      )
      .finally(() => setLoading(false));
    // i18n is stable; intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep <html lang> and <html dir> in sync with the active language so
  // assistive tech and CSS logical properties / `rtl:` Tailwind variants
  // resolve correctly.
  useEffect(() => {
    const lang = i18n.language;
    document.documentElement.lang = lang;
    document.documentElement.dir = isSupportedLocale(lang) && isRtl(lang) ? 'rtl' : 'ltr';
  }, [i18n.language]);

  if (loading) return <LoadingScreen />;
  if (error || !context)
    return <ErrorScreen message={error ?? 'Unable to resolve project context'} />;

  return (
    <Routes>
      <Route path="/" element={<StartPage context={context} />} />
      <Route path="/resume" element={<CaseResumePage context={context} />} />
      <Route path="/resubmit" element={<ResubmissionPage context={context} />} />
      <Route
        path="/case/:caseId/details"
        element={<PersonalDetailsPage context={context} />}
      />
      <Route
        path="/case/:caseId/documents"
        element={<UploadDocumentsPage context={context} />}
      />
      <Route
        path="/case/:caseId/selfie"
        element={<SelfiePage context={context} />}
      />
      <Route
        path="/case/:caseId/confirmation"
        element={<ConfirmationPage context={context} />}
      />
      <Route
        path="/case/:caseId/business-verification"
        element={<BusinessVerificationPage branding={context.branding} />}
      />
      <Route
        path="/case/:caseId/merchant-onboarding"
        element={<MerchantOnboardingPage />}
      />
      <Route
        path="/case/:caseId/step-up"
        element={<StepUpPage context={context} />}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
